import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { AppConstants, RegexConstants } from '@wz-constants';
import { CarPickerService } from '@wz-services';
import { formatDate, getDateUtc } from '@wz-utilities/date.utilities';
import { getAllControlsFiltered } from '@wz-utilities/form.utilities';
import { simpleEquals } from '@wz-utilities/object.utilities';
import { WzDateValidators } from '@wz-validators';
import { addDays, isBefore } from 'date-fns';
import { PolicyModels } from 'models/policy.models';
import { VehicleModels } from 'models/vehicle.models';
import { forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  finalize,
  map,
  mergeMap,
  tap,
} from 'rxjs/operators';
import {
  CarPickerData,
  ListInfo,
} from '../../../../../models/car-picker.model';

@Component({
  selector: 'wz-car-picker',
  templateUrl: './car-picker.component.html',
  styleUrls: ['./car-picker.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CarPickerComponent),
      multi: true,
    },
  ],
})
export class CarPickerComponent implements OnInit, ControlValueAccessor {
  @Input()
  dateAchatDaysInFutureAllowed: number = 0;
  @Input()
  zipCode!: string;
  @Input() searchByTypeMine = false;

  allowTriggerValueChanges = true;
  carGroup: FormGroup;

  isDisabled!: boolean;
  isSearchingByImmat = false;
  isFormShown = false;
  wasSearchTriggered = false;
  isFillingForm = false;
  isEditMode = true;
  hasImmatChanged = false;

  proposedVehicleType!: 'immatriculation' | 'type mine' | null;
  proposedVehicle!: PolicyModels.VehicleDetails | null;
  confirmedVehicle!: PolicyModels.VehicleDetails | null;

  marqueListInfo: ListInfo = { list: [], isLoading: false };
  modeleListInfo: ListInfo = { list: [], isLoading: false };
  energieListInfo: ListInfo = { list: [], isLoading: false };
  chevauxFiscauxListInfo: ListInfo = { list: [], isLoading: false };
  carrosserieListInfo: ListInfo = { list: [], isLoading: false };
  versionListInfo: ListInfo = { list: [], isLoading: false };

  immatriculationCtrl!: AbstractControl | any;
  dateMiseEnCirculationCtrl!: AbstractControl | any;
  dateAchatVehiculeCtrl!: AbstractControl | any;
  marqueCtrl!: AbstractControl | any;
  modeleCtrl!: AbstractControl | any;
  energieCtrl!: AbstractControl | any;
  chevauxFiscauxCtrl!: AbstractControl | any;
  carrosserieCtrl!: AbstractControl | any;
  versionCtrl!: AbstractControl | any;

  typeMineCtrl!: FormControl;

  private lastValue!: CarPickerData;

  constructor(
    private fb: FormBuilder,
    private carService: CarPickerService,
  ) {
    this.carGroup = this.createForm();
    this.createCtrlRefs();
  }

  ngOnInit(): void {
    this.carService.getMarques().subscribe((list) => {
      this.marqueListInfo.list = list;
    });

    //  Immatriculation search
    this.immatriculationCtrl.valueChanges
      .pipe(
        filter(
          (_) =>
            this.immatriculationCtrl.valid && this.allowTriggerValueChanges,
        ),
        tap(() => {
          this.hasImmatChanged = true;
          this.proposedVehicle = null;
          this.proposedVehicleType = null;
          this.isSearchingByImmat = true;
        }),
        mergeMap((immat: any) =>
          this.carService
            .getInformationsFromImmatriculation(immat)
            .pipe(catchError((_e) => of(null))),
        ),
      )
      .subscribe((car: PolicyModels.VehicleDetails | null) => {
        this.isSearchingByImmat = false;
        if (car != null) {
          this.proposedVehicleType = 'immatriculation';
          this.proposedVehicle = car;
          this.validateProposedVehicle();
        } else {
          this.proposedVehicle = null;
          this.proposedVehicleType = null;
          this.isFormShown = true;
        }
      });

    // Type Mine search
    this.typeMineCtrl = this.fb.control('', [
      Validators.required,
      Validators.minLength(6),
    ]);
    this.typeMineCtrl.valueChanges
      .pipe(
        debounceTime(AppConstants.Delay.VeryFast),
        tap(() => {
          this.hasImmatChanged = true;
          this.proposedVehicle = null;
          this.proposedVehicleType = null;
          this.isSearchingByImmat = true;
        }),
        mergeMap((typeMine) => {
          if (this.typeMineCtrl.valid) {
            return this.carService.getInformationsFromTypeMine(typeMine).pipe(
              catchError((_e) => {
                return of(null);
              }),
            );
          }

          return of(null);
        }),
      )
      .subscribe((car: PolicyModels.VehicleDetails | null) => {
        this.isSearchingByImmat = false;
        if (car != null) {
          this.proposedVehicle = car;
          this.proposedVehicleType = 'type mine';
        } else {
          this.proposedVehicle = null;
          this.proposedVehicleType = null;
        }
      });

    // Load versions when DateMiseEnCirculation changes
    this.dateMiseEnCirculationCtrl.valueChanges
      .pipe(
        filter(
          () =>
            this.allowTriggerValueChanges &&
            this.dateMiseEnCirculationCtrl.valid &&
            this.carrosserieCtrl.valid,
        ),
        mergeMap((dateMiseEnCirculation) => {
          this.versionListInfo.isLoading = true;
          let value = { ...this.carGroup.value, dateMiseEnCirculation };
          return this.carService.getVersions(value).pipe(
            catchError((_e) => of([])),
            finalize(() => (this.versionListInfo.isLoading = false)),
          );
        }),
      )
      .subscribe((res: any) => {
        this.versionListInfo.list = res;
        this.versionCtrl.setValue(null);
      });

    this.subscribeToAncestor(
      this.modeleCtrl,
      this.marqueCtrl,
      (req) => this.carService.getModeles(req),
      this.modeleListInfo,
    );

    this.subscribeToAncestor(
      this.energieCtrl,
      this.modeleCtrl,
      (req) => this.carService.getEnergie(req),
      this.energieListInfo,
    );

    this.subscribeToAncestor(
      this.chevauxFiscauxCtrl,
      this.energieCtrl,
      (req) => this.carService.getChevauxFiscaux(req),
      this.chevauxFiscauxListInfo,
    );

    this.subscribeToAncestor(
      this.carrosserieCtrl,
      this.chevauxFiscauxCtrl,
      (req) => this.carService.getCarrosseries(req),
      this.carrosserieListInfo,
    );

    this.subscribeToAncestor(
      this.versionCtrl,
      this.carrosserieCtrl,
      (req) => this.carService.getVersions(req),
      this.versionListInfo,
    );
  }

  customizeForTunnelApp() {
    this.carGroup.statusChanges.subscribe((x) => {
      if (x === 'VALID') {
        this.submit();
      }
    });
  }

  createForm() {
    return this.fb.group({
      immatriculation: this.fb.control('', [
        Validators.required,
        Validators.pattern(RegexConstants.CarImmatriculation),
      ]),
      dateMiseEnCirculation: this.fb.control('', [
        Validators.required,
        WzDateValidators.max(getDateUtc() ?? new Date()),
      ]),
      dateAchatVehicule: this.fb.control('', [
        Validators.required,
        WzDateValidators.minControl('dateMiseEnCirculation'),
        WzDateValidators.filter(
          (date: Date | null) =>
            date != null &&
            isBefore(
              date,
              addDays(
                getDateUtc() ?? new Date(),
                this.dateAchatDaysInFutureAllowed,
              ),
            ),
          'max',
        ),
      ]),
      marque: this.fb.control('', [Validators.required]),
      modele: this.fb.control('', [Validators.required]),
      energie: this.fb.control('', [Validators.required]),
      chevauxFiscaux: this.fb.control('', [Validators.required]),
      carrosserie: this.fb.control('', [Validators.required]),
      version: this.fb.control('', [Validators.required]),
    });
  }

  submit() {
    if (this.carGroup.invalid) {
      alert('oups invalid');
      return;
    }

    const value: CarPickerData = {
      codeGta: this.versionCtrl.value.codeGta,
      immatriculation: this.immatriculationCtrl.value,
      dateAchatVehicule: this.dateAchatVehiculeCtrl.value,
      dateMiseEnCirculation: this.dateMiseEnCirculationCtrl.value,
      typeMine: this.versionCtrl.value.typeMine,
    };

    if (!simpleEquals(this.lastValue, value)) {
      this.propagateChange(value);
      this.lastValue = value;
    }

    const form = this.carGroup.value as VehicleModels.Form;
    this.confirmedVehicle = this.getVehicleDetailsFromForm(form);
    this.isEditMode = false;
  }

  goEditMode() {
    this.isEditMode = true;
  }

  cancelEdit() {
    this.fillForm(
      this.getFormFromVehicleDetails(this.confirmedVehicle!),
    ).subscribe((_) => {
      this.isEditMode = false;
    });
  }

  doNotUseNewProposedVehicle() {
    this.hasImmatChanged = false;
  }

  validateProposedVehicle() {
    const formData: VehicleModels.Form = this.getFormFromVehicleDetails(
      this.proposedVehicle!,
    );

    formData.dateAchatVehicule = this.dateAchatVehiculeCtrl.value;
    formData.dateMiseEnCirculation =
      formatDate(this.proposedVehicle!.dateMiseEnCirculation!) ||
      this.dateMiseEnCirculationCtrl.value;

    formData.typeMine = this.proposedVehicle!.typeMine;

    this.fillForm(formData).subscribe((form) => {
      this.allowTriggerValueChanges = false;
      if (!form.carrosserie && this.carrosserieListInfo.list.length === 1) {
        this.carrosserieCtrl.setValue(this.carrosserieListInfo.list[0].value);
      }
      this.allowTriggerValueChanges = true;
    });
  }

  start() {
    this.isFormShown = true;
    this.hasImmatChanged = false;
  }

  getAllList(search: VehicleModels.Search): Observable<void> {
    return forkJoin(
      this.carService.getModeles(search),
      this.carService.getEnergie(search),
      this.carService.getChevauxFiscaux(search),
      this.carService.getCarrosseries(search),
      this.carService.getVersions(search),
    ).pipe(
      map((arrays) => {
        this.modeleListInfo.list = arrays[0];
        this.energieListInfo.list = arrays[1];
        this.chevauxFiscauxListInfo.list = arrays[2];
        this.carrosserieListInfo.list = arrays[3];
        this.versionListInfo.list = arrays[4];
      }),
    );
  }

  resetAllList() {}

  shouldDisplayHint(listInfo: ListInfo) {
    return !listInfo.isLoading && listInfo.list && listInfo.list.length > 0;
  }

  getListHint(listInfo: ListInfo, word: string) {
    if (!listInfo.list || listInfo.list.length === 0) {
      return 'Aucun choix disponible';
    }

    const plural = listInfo.list.length > 1 ? 's' : '';
    return `${listInfo.list.length} ${word}${plural} disponible${plural}`;
  }

  /** ControlValueAccessor implem */
  writeValue(value: CarPickerData): void {
    this.lastValue = value;
    if (value) {
      this.isEditMode = false;
      this.isFillingForm = true;
      this.confirmedVehicle = null;
      this.allowTriggerValueChanges = false;
      this.carGroup.patchValue({
        immatriculation: value.immatriculation,
      });

      this.carService
        .getInformationsFromCodeGta(value.codeGta)
        .pipe(
          mergeMap((v: PolicyModels.VehicleDetails) => {
            v.immatriculation = value.immatriculation;
            v.dateAchatVehicule = getDateUtc(value.dateAchatVehicule)!;
            v.dateMiseEnCirculation = getDateUtc(value.dateMiseEnCirculation)!;

            this.confirmedVehicle = v;
            return this.fillForm(this.getFormFromVehicleDetails(v));
          }),
          finalize(() => (this.isFillingForm = false)),
        )
        .subscribe((_v) => {});
    }
  }

  getFormFromVehicleDetails(
    vd: PolicyModels.VehicleDetails,
  ): VehicleModels.Form | any {
    return {
      carrosserie: vd.carrosserie,
      chevauxFiscaux: vd.chevauxFiscaux,
      dateAchatVehicule: formatDate(vd.dateAchatVehicule!)!,
      dateMiseEnCirculation: formatDate(vd.dateMiseEnCirculation!)!,
      energie: vd.energie,
      marque: vd.marque,
      modele: vd.modele,
      version: vd.version
        ? {
            codeGta: vd.codeGta,
            typeMine: vd.typeMine,
            version: vd.version,
          }
        : null,
    };
  }

  getVehicleDetailsFromForm(
    form: VehicleModels.Form,
  ): PolicyModels.VehicleDetails | any {
    return {
      carrosserie: form.carrosserie,
      chevauxFiscaux: form.chevauxFiscaux,
      dateAchatVehicule: getDateUtc(form.dateAchatVehicule),
      dateMiseEnCirculation: getDateUtc(form.dateMiseEnCirculation),
      energie: form.energie,
      marque: form.marque,
      modele: form.modele,
      codeGta: form.version.codeGta,
      typeMine: form.version.typeMine,
      version: form.version.version,
      immatriculation: form.immatriculation,
    };
  }

  propagateChange = (_: any) => {};

  registerOnChange(fn: (_: any) => void) {
    this.propagateChange = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.carGroup.disable();
    } else {
      this.carGroup.enable();
    }
  }
  registerOnTouched(_fn: any): void {}

  /** End ControlValueAccessor implem */

  private createCtrlRefs() {
    this.immatriculationCtrl = this.carGroup.get('immatriculation');
    this.dateMiseEnCirculationCtrl = this.carGroup.get('dateMiseEnCirculation');
    this.dateAchatVehiculeCtrl = this.carGroup.get('dateAchatVehicule');
    this.marqueCtrl = this.carGroup.get('marque');
    this.modeleCtrl = this.carGroup.get('modele');
    this.energieCtrl = this.carGroup.get('energie');
    this.chevauxFiscauxCtrl = this.carGroup.get('chevauxFiscaux');
    this.carrosserieCtrl = this.carGroup.get('carrosserie');
    this.versionCtrl = this.carGroup.get('version');
  }

  private subscribeToAncestor(
    selfControl: AbstractControl,
    ancestorControl: AbstractControl,
    onFetch: (m: VehicleModels.Search) => Observable<any>,
    dataList: ListInfo,
  ) {
    ancestorControl.valueChanges
      .pipe(
        filter(() => this.allowTriggerValueChanges),
        tap(() => {
          if (dataList) {
            dataList.isLoading = true;
          }

          selfControl.setValue('');
        }),
        mergeMap(() => {
          if (ancestorControl.valid) {
            if (onFetch) {
              return onFetch(this.carGroup.value).pipe(
                catchError((_e) => of([])),
              );
            } else {
              return of([]);
            }
          } else {
            return of([]);
          }
        }),
      )
      .subscribe((selectListItems) => {
        if (dataList) {
          dataList.isLoading = false;
          dataList.list = selectListItems;
        }
      });
  }

  private fillForm(formData: VehicleModels.Form) {
    this.start();
    this.allowTriggerValueChanges = false;

    return this.getAllList(formData).pipe(
      map(() => {
        if (formData.version) {
          const existing = this.versionListInfo.list.find(
            (v) => v.value.version === formData.version.version,
          );
          if (existing) {
            formData.version = existing.value;
          }
        }

        this.carGroup.patchValue(formData);
        getAllControlsFiltered(this.carGroup, null, [
          'immatriculation',
        ]).forEach((c) => {
          c.markAsDirty();
          c.updateValueAndValidity();
        });
        this.allowTriggerValueChanges = true;
        return formData;
      }),
    );
  }
}
